// Shared sidebar dropdown — rendered at page level to escape overflow/opacity.
// Controlled via root.sidebarDropdown state set by SidebarItemMore.onClick.
// Always in DOM but off-screen when closed — avoids first-render flash.
//
// Position + opacity are reactive DOMQL props (not inline style side-effects).
// This works around a DOMQL v3.14 quirk where `onUpdate` doesn't re-fire when
// only a reactive `children:` factory changes — the dropdown used to render
// its new children correctly but keep its position stuck at -9999px because
// the position writer was tied to `onUpdate`, not the reactive state read.

const _resolvePos = (s) => {
  const dd = s?.root?.sidebarDropdown
  if (!dd?.open) return { top: -9999, left: -9999, opacity: 0, pointerEvents: 'none' }

  let top = dd.top || 0
  let left = dd.left || 0

  // We can't measure self-dimensions here (runs on every root update, before
  // layout). Use conservative defaults + real window bounds. The first render
  // places the menu; on the rare edge-overflow case a scroll handler corrects.
  const ddH = 200
  const ddW = 200
  if (typeof window !== 'undefined') {
    if (top + ddH > window.innerHeight) top = (dd.btnTop || top) - ddH - 2
    if (left + ddW > window.innerWidth) left = window.innerWidth - ddW - 4
    if (left < 4) left = 4
  }
  return { top, left, opacity: 1, pointerEvents: 'auto' }
}

export const ChatSidebarDropdown = {
  position: 'fixed',
  zIndex: 9999,
  flow: 'y',
  minWidth: 'F',
  padding: 'V',
  round: 'Z',
  background: 'overlayStrong',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  boxShadow: '0 8px 24px -4px rgba(0, 0, 0, 0.5)',

  // Reactive positioning — re-evaluates on every root state change.
  top: (el, s) => _resolvePos(s).top + 'px',
  left: (el, s) => _resolvePos(s).left + 'px',
  opacity: (el, s) => String(_resolvePos(s).opacity),
  pointerEvents: (el, s) => _resolvePos(s).pointerEvents,

  onCreate: (el, s) => {
    // Close on outside click. Uses capture so it runs before inner click
    // handlers and won't race with MoreBtn's own click (which carries a
    // `data-key="MoreBtn"` that we match on here to exempt).
    el._clickAway = (ev) => {
      if (!el.node) return
      if (el.node.contains(ev.target)) return
      if (ev.target.closest('[data-key="MoreBtn"]')) return
      if (s.root?.sidebarDropdown?.open) {
        s.root.update({ sidebarDropdown: { open: false } })
      }
    }
    setTimeout(() => document.addEventListener('click', el._clickAway, true), 0)
  },

  onRemove: (el) => {
    if (el._clickAway) document.removeEventListener('click', el._clickAway, true)
  },

  childExtends: 'ChatSidebarDropdownItem',
  childrenAs: 'state',
  children: (el, s) => {
    const dd = s.root?.sidebarDropdown
    return (dd?.items || []).map(item => ({
      ...item,
      _channelId: dd?.channelId
    }))
  }
}

export const ChatSidebarDropdownItem = {
  tag: 'button',
  type: 'button',
  padding: 'Z A',
  round: 'V',
  background: 'transparent',
  borderWidth: '0',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  color: (el, s) => s.color || 'title',
  ':hover': { background: 'overlay' },
  text: (el, s) => s.label || '',

  onClick: (e, el, s) => {
    e.stopPropagation()
    const action = s.action
    const channelId = s._channelId
    const root = s.root

    root.update({ sidebarDropdown: { open: false } })

    if (!action || !channelId) return

    if (action === 'rename-channel') {
      const ch = (root.channels || []).find(c => c.id === channelId)
      root.update({
        renameModal: { open: true, channelId, currentName: ch?.name || '' }
      })
    } else if (action === 'hide-dm') {
      el.call('hideDM', channelId)
    } else if (action === 'leave-channel') {
      el.call('leaveChannel', channelId)
    } else if (action === 'pin-to-top') {
      el.call('pinToTop', channelId)
    } else if (action === 'invite-channel' || action === 'channel-settings') {
      el.call('openChannelSettings', channelId)
    } else if (action === 'start-group-chat') {
      el.call('startGroupChat', channelId)
    } else if (action === 'convert-to-channel') {
      el.call('convertToChannel', channelId)
    }
  }
}
